import React, { useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { IconArrowLeftSlide, IconArrowRightSlide, Play } from '../icons/MainIcons';
import { getStorageUrl } from '@/utils/utils';

type Slides = {
    content?: string | null;
    image?: string;
    video?: string;
};

type SlidesProps = {
    slides: Slides[] | undefined;
    className?: string;
};

const getFileType = (url: string): 'image' | 'video' | 'unknown' => {
    const extension = url.split('.').pop()?.toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension || '')) {
        return 'image';
    } else if (['mp4', 'webm', 'ogg'].includes(extension || '')) {
        return 'video';
    }
    return 'unknown';
};

const ImgSlider: React.FC<SlidesProps> = ({ slides, className = '' }) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const togglePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying); // تبديل حالة التشغيل
        }
    };

    useEffect(() => {
        return () => {
            if (videoRef.current) {
                videoRef.current.pause();
            }
        };
    }, [selectedIndex]);

    const [emblaRefThumbnails, emblaApiThumbnails] = useEmblaCarousel({
        loop: true,
        dragFree: true,
        containScroll: 'trimSnaps',
    });

    const onSelectThumbnail = () => {
        if (!emblaApiThumbnails) return;
        const newIndex = emblaApiThumbnails.selectedScrollSnap();
        setSelectedIndex(newIndex);
    };

    useEffect(() => {
        if (!emblaApiThumbnails) return;
        emblaApiThumbnails.on('select', onSelectThumbnail);
        onSelectThumbnail();
    }, [emblaApiThumbnails]);

    const goToSlide = (index: number) => {
        setSelectedIndex(index);
        emblaApiThumbnails?.scrollTo(index);
    };

    return (
        <div className="relative">
            <div className="w-full h-auto">
                {slides && slides.length > 0 ? (
                    slides.map((e, i) => (
                        <div key={i} className={`${i === selectedIndex ? 'block' : 'hidden'}`}>
                            {e.image && getFileType(e.image) === 'image' && (
                                <div className="w-full lg:h-[560px] md:h-[490px] sm:h-[400px] h-[300px] relative">
                                    <img
                                        src={getStorageUrl(e.image)}
                                        alt={`Slide ${i}`}
                                        className="w-full h-full object-cover lg:rounded-[40px] md:rounded-[30px] rounded-[20px] border border-white/20"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent lg:rounded-[40px] md:rounded-[30px] rounded-[20px]"></div>

                                    {e.content && (
                                        <p
                                            dir="auto"
                                            className="imgTxt lg:line-clamp-none line-clamp-2 w-full absolute sm:bottom-[60px] bottom-[17px] sm:px-7 px-5"
                                        >
                                            {e.content}
                                        </p>
                                    )}
                                </div>
                            )}

                            {e.image && getFileType(e.image) === 'video' && (
                                <div
                                    className="w-full lg:h-[560px] md:h-[490px] sm:h-[400px] h-[300px] relative cursor-pointer"
                                    onClick={togglePlayPause}
                                >
                                    <video
                                        src={getStorageUrl(e.image)}
                                        ref={videoRef}
                                        onClick={togglePlayPause}
                                        className="w-full h-full object-cover lg:rounded-[40px] md:rounded-[30px] rounded-[20px] border border-white/20"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent lg:rounded-[40px] md:rounded-[30px] rounded-[20px]"></div>

                                    {e.content && (
                                        <p
                                            dir="auto"
                                            className="imgTxt lg:line-clamp-none line-clamp-2 w-full absolute sm:bottom-[60px] bottom-[17px] sm:px-7 px-5 text-white"
                                        >
                                            {e.content}
                                        </p>
                                    )}

                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="lg:p-3 md:p-[10px] p-2 rounded-full bg-white ">
                                            <Play className="lg:w-[32px] md:w-[28px] sm:w-[24px] w-[20px] lg:h-[32px] md:h-[28px] sm:h-[24px] h-[20px]" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                ) : (
                    <p>لا توجد بيانات لعرضها.</p>
                )}
            </div>

            {slides && slides.length > 0 && (
                <div
                    ref={emblaRefThumbnails}
                    className="embla hidden sm:flex mx-auto items-center lg:max-w-[860px] md:max-w-[620px] max-w-[550px] px-0 md:px-0 sm:px-10 absolute bottom-[50px] left-0 right-0"
                >
                    <div
                        className={`w-full flex ${className}`}
                        style={{ gap: '10px', paddingLeft: '10px', paddingRight: '10px' }}
                    >
                        {slides.map((e, i) => (
                            <div
                                key={i}
                                className="embla__slide cursor-pointer max-w-[38%] md:max-w-[33.33%]"
                                onClick={() => goToSlide(i)}
                            >
                                <div className="w-full h-full bg-cover max-w-[170px] max-h-[118px] rounded-[16px]">
                                    {e.image && getFileType(e.image) === 'image' ? (
                                        <img
                                            src={getStorageUrl(e.image)}
                                            alt={`Thumbnail ${i}`}
                                            className="w-full h-full object-cover rounded-[16px]"
                                        />
                                    ) : e.image && getFileType(e.image) === 'video' ? (
                                        <div className="w-full h-full flex items-center justify-center bg-black/50 rounded-[16px]">
                                            <video
                                                src={getStorageUrl(e.image)}
                                                // controls
                                                className="w-full h-full object-cover rounded-[16px]"
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Pagination */}
            <div className="sm:hidden flex items-center justify-center gap-4 relative lg:mt-[32px] md:mt-[25px] sm:mt-[18px] mt-[12px]">
                <button onClick={() => emblaApiThumbnails?.scrollNext()} className="arrowSlideProject">
                    <IconArrowRightSlide className="iconArrow" />
                </button>
                <div className="embla__pagination flex justify-center gap-1">
                    {slides
                        ?.slice(Math.min(selectedIndex, slides.length - 3), Math.min(selectedIndex + 3, slides.length))
                        .map((_, index) => {
                            const realIndex = Math.min(selectedIndex, slides.length - 3) + index;

                            return (
                                <button
                                    key={realIndex}
                                    onClick={() => goToSlide(realIndex)}
                                    className={`embla__dot mx-1 rounded-full lg:h-3 h-2 dark:bg-Grey20 bg-embla-dot ${
                                        realIndex === selectedIndex
                                            ? 'opacity-90 xl:w-[73px] lg:w-[65px] md:w-[55px] sm:w-[40px] w-[24px]'
                                            : 'opacity-30 lg:w-[26px] md:w-[22px] sm:w-[17px] w-[14px]'
                                    }`}
                                ></button>
                            );
                        })}
                </div>

                <button onClick={() => emblaApiThumbnails?.scrollPrev()} className="arrowSlideProject">
                    <IconArrowLeftSlide className="iconArrow" />
                </button>
            </div>
        </div>
    );
};

export default ImgSlider;
